/* MUSER cart helpers (2026-10-01).
   Liquid has no exchange rate, so anything quoted in USD (the $75 free-shipping minimum, the $4.95 flat rate)
   is converted here with Shopify.currency.rate and formatted in the shopper's currency.
   - <m4-money data-shop-cents="495">  -> "$4.95" / "4,30 €"
   - <m4-ship-bar data-threshold="7500" data-subtotal="{cart cents}" data-total="{cart cents}">: sets the bar, the message,
     and announces the new subtotal in the drawer's live region. Custom elements upgrade every time the theme swaps
     the cart HTML in, so the numbers stay right after +, - and Remove in every currency.
   - /cart page: dims the totals while a change is in flight (the page used to show the old subtotal for 1-2 s). */
(() => {
  const S = () => window.Shopify || {};
  const rate = () => parseFloat((S().currency && S().currency.rate) || 1) || 1;
  const cur = () => (S().currency && S().currency.active) || 'USD';
  // Format exactly like the store's own money format for this currency: Liquid prints 123456 with the
  // `money` filter as a sample ("$1,234.56", "€1.234,56", "1 234,56 kr"); we reuse its symbol, separators and order.
  const fmt = (cents) => {
    const el = document.querySelector('[data-m4-money-sample]');
    const m = el && el.getAttribute('data-m4-money-sample').match(/^(.*?)1(\D?)234(?:(\D)56)?(.*)$/);
    const v = Math.round(cents);
    if (m) {
      const [, pre, th, dec, post] = m;
      const whole = String(Math.floor(v / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return pre + whole + (dec ? dec + String(v % 100).padStart(2, '0') : '') + post;
    }
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur() }).format(v / 100); }
    catch (e) { return (v / 100).toFixed(2) + ' ' + cur(); }
  };
  let settled = false;   // no "bag updated" announcement for the first render on page load
  addEventListener('load', () => setTimeout(() => { settled = true; }, 800));
  const conv = (shopCents) => Math.round(shopCents * rate());

  if (!customElements.get('m4-money')) customElements.define('m4-money', class extends HTMLElement {
    connectedCallback() { const c = Number(this.dataset.shopCents); if (!Number.isNaN(c)) this.textContent = fmt(conv(c)); }
  });

  if (!customElements.get('m4-ship-bar')) customElements.define('m4-ship-bar', class extends HTMLElement {
    connectedCallback() {
      const t = conv(Number(this.dataset.threshold || 7500));
      const s = Number(this.dataset.subtotal || 0);
      const p = Math.max(0, Math.min(1, t ? s / t : 0));
      const bar = this.querySelector('.hdt-progress-bar');
      if (bar) {
        bar.setAttribute('data-hdt-percent', String(p));
        bar.style.setProperty('--progress-rate', String(p));
        bar.setAttribute('aria-valuemax', String(t));
        bar.setAttribute('aria-valuenow', String(Math.min(s, t)));
        bar.setAttribute('aria-valuetext', p >= 1 ? 'Free shipping reached' : fmt(t - s) + ' to free shipping');
      }
      const wrap = this.querySelector('.hdt-free-shipping-bar');
      if (wrap) wrap.className = wrap.className.replace(/\bis-(intial|unreached|success)\b/, 'is-' + (p >= 1 ? 'success' : p <= 0.7 ? 'intial' : 'unreached'));
      const msg = this.querySelector('[data-m4-ship-msg]');
      if (msg) msg.textContent = p >= 1 ? 'Shipping is free on this order.'
        : s <= 0 ? 'Free shipping over ' + fmt(t) + '.'
        : fmt(t - s) + ' more and shipping is free.';
      // announce the new subtotal once per re-render (the theme replaces the visible totals wholesale)
      const live = document.querySelector('[data-m4-cart-live]');
      if (settled && live && this.dataset.total && this.closest('#CartDrawer')) {
        live.textContent = 'Bag updated. Subtotal ' + fmt(Number(this.dataset.total)) + '.';
      }
      document.querySelectorAll('[data-m4-busy]').forEach((el) => { el.removeAttribute('data-m4-busy'); el.removeAttribute('aria-busy'); });
    }
  });

  // <m4-eta> (CRO audit 2026-10 F4; rendered only when theme setting m4_delivery_dates is on): turns the product page's
  // "made in X days" + "then Y days" lines into one estimated delivery range from today's date. Window = the shipping
  // policy's business days: Oct-Dec US 8-18 / EU 12-30; Jan-Sep printed US 5-15 / EU 9-27, embroidered US 6-15 / EU 10-27.
  // Counting starts the business day after today; weekends, US federal holidays (observed) and Printful's EU closures
  // (24-26 Dec, 31 Dec-1 Jan) are skipped for every market, so a date is never earlier than the policy allows.
  const nthWeekday = (y, m, wd, n) => { const d = new Date(y, m, 1); d.setDate(1 + ((wd - d.getDay() + 7) % 7) + (n - 1) * 7); return d; };
  const lastWeekday = (y, m, wd) => { const d = new Date(y, m + 1, 0); d.setDate(d.getDate() - ((d.getDay() - wd + 7) % 7)); return d; };
  const observed = (d) => { const x = new Date(d); if (x.getDay() === 6) x.setDate(x.getDate() - 1); else if (x.getDay() === 0) x.setDate(x.getDate() + 1); return x; };
  const key = (d) => d.getFullYear() + '-' + d.getMonth() + '-' + d.getDate();
  const holidayCache = {};
  const holidays = (y) => holidayCache[y] || (holidayCache[y] = new Set([
    observed(new Date(y, 0, 1)), nthWeekday(y, 0, 1, 3), nthWeekday(y, 1, 1, 3), lastWeekday(y, 4, 1), observed(new Date(y, 5, 19)),
    observed(new Date(y, 6, 4)), nthWeekday(y, 8, 1, 1), nthWeekday(y, 9, 1, 2), observed(new Date(y, 10, 11)), nthWeekday(y, 10, 4, 4),
    observed(new Date(y, 11, 25)), new Date(y, 11, 24), new Date(y, 11, 26), new Date(y, 11, 31), new Date(y, 0, 1)
  ].map(key)));
  const addBusinessDays = (from, n) => {
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    let left = n;
    while (left > 0) { d.setDate(d.getDate() + 1); const wd = d.getDay(); if (wd !== 0 && wd !== 6 && !holidays(d.getFullYear()).has(key(d))) left--; }
    return d;
  };
  if (!customElements.get('m4-eta')) customElements.define('m4-eta', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.done) return;
      const us = this.dataset.market !== 'eu';
      const today = new Date();
      const q4 = today.getMonth() >= 9;
      const min = (q4 ? 5 : (this.dataset.emb === '1' ? 3 : 2)) + (us ? 3 : 7);
      const max = us ? (q4 ? 18 : 15) : (q4 ? 30 : 27);
      let f;
      try { f = new Intl.DateTimeFormat(us ? 'en-US' : 'en-GB', { month: 'short', day: 'numeric' }); } catch (e) { return; }
      const a = f.format(addBusinessDays(today, min)), b = f.format(addBusinessDays(today, max));
      const policy = this.dataset.policy || '/policies/shipping-policy';
      this.innerHTML = '<strong>Made to order.</strong> Estimated delivery ' + a + ' to ' + b + '. <a href="' + policy + '">How shipping works</a>';
      this.dataset.done = '1';
      const next = this.closest('li') && this.closest('li').nextElementSibling;
      if (next && !next.querySelector('m4-eta')) next.hidden = true;   // the "then Y business days" line
    }
  });

  // /cart page: show that the total is recalculating instead of leaving a stale number on screen
  document.addEventListener('click', (e) => {
    const hit = e.target.closest('.hdt-page-cart .hdt-quantity__button, .hdt-page-cart wrapp-remove-item-oncart, .hdt-page-cart [name="minus"], .hdt-page-cart [name="plus"]');
    if (!hit) return;
    const box = document.querySelector('.hdt-page-cart__footer');
    if (!box) return;
    box.setAttribute('data-m4-busy', '');
    box.setAttribute('aria-busy', 'true');
    setTimeout(() => { box.removeAttribute('data-m4-busy'); box.removeAttribute('aria-busy'); }, 6000);
  }, true);
})();

/* Own apps #4 (2026-10-14): one-tap add for the drawer add-on (snippets/m4-cart-addon.liquid) and the /cart page
   suggestions (snippets/m4-cart-recs.liquid). Posts the variant to /cart/add.js, then fires the theme's own
   cart:reload so the drawer / cart page re-render from Shopify (count, bar, totals, and the add-on disappears). */
(() => {
  if (window.__m4AddOn) return;
  window.__m4AddOn = true;
  document.addEventListener('click', async (ev) => {
    const btn = ev.target.closest('[data-m4-addon-add]');
    if (!btn || btn.disabled) return;
    ev.preventDefault();
    const box = btn.closest('.m4-addon, .m4-recs__item') || btn.parentElement;
    const msg = box && box.querySelector('[data-m4-addon-msg]');
    const label = btn.textContent;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = 'Adding';
    try {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const r = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // _src (underscore properties never show in cart or checkout) tells the dashboard which own app sold it
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.m4AddonAdd), quantity: 1,
          properties: { _src: btn.closest('.m4-recs__item') ? 'recs' : 'addon' } }] })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.status) throw new Error(data.description || data.message || 'add failed');
      btn.textContent = 'Added';
      if (msg) msg.textContent = 'Added to your bag.';
      document.dispatchEvent(new CustomEvent('cart:reload'));
      if (btn.hasAttribute('data-m4-reload')) setTimeout(() => location.reload(), 300);
    } catch (e) {
      btn.disabled = false;
      btn.textContent = label;
      if (msg) msg.textContent = 'Could not add it. Try again, or open the product page.';
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });
})();

/* Own apps #4 (2026-10-14): <m4-cart-recs> on /cart. Complementary picks first (Search & Discovery), then Shopify's
   related picks; sections/m4-recs.liquid drops items already in the bag and prints nothing when none are left. */
if (!customElements.get('m4-cart-recs')) customElements.define('m4-cart-recs', class extends HTMLElement {
  async connectedCallback() {
    if (this.dataset.done) return;
    this.dataset.done = '1';
    const id = this.dataset.productId;
    if (!id) return;
    const base = this.dataset.root || '/recommendations/products';
    for (const intent of ['complementary', 'related']) {
      try {
        const r = await fetch(`${base}?section_id=m4-recs&product_id=${encodeURIComponent(id)}&limit=8&intent=${intent}`);
        if (!r.ok) continue;
        const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
        const box = doc.querySelector('.m4-recs[data-count]');
        if (box) { this.replaceChildren(document.importNode(box, true)); return; }
      } catch (e) { /* leave the slot empty */ }
    }
  }
});
